import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Container from '~/components/Container';
import Button from '~/components/Button';
import { useTitle } from '~/lib/useTitle';
import s from './ApiReference.module.scss';

const BASE_URL = 'https://www.metahub.space';

const FIND_SAMPLE = `[
  {
    "imdb_id": "tt28014327",
    "name": "Mayday",
    "type": "movie",
    "cast": [
      "Ryan Reynolds",
      "Kenneth Branagh",
      "Marcin Dorocinski"
    ],
    "country": "United States",
    "description": "Lieutenant Troy Brennan's reconnaissance mission over Soviet territory goes awry, forcing him to crash-land and survive in Russian wilderness while evading capture or rescue.",
    "director": [
      "John Francis Daley",
      "Jonathan Goldstein"
    ],
    "genre": [
      "Action",
      "Adventure",
      "Comedy"
    ],
    "released": "2026-09-04T00:00:00.000Z",
    "writer": [
      "John Francis Daley",
      "Jonathan Goldstein"
    ],
    "year": "2026",
    "moviedb_id": 1137844,
    "runtime": "111 min",
    "trailers": [
      {
        "source": "om5Un9X720M",
        "type": "Trailer"
      }
    ],
    "id": "tt28014327",
    "genres": [
      "Action",
      "Adventure",
      "Comedy"
    ],
    "releaseInfo": "2026"
  }
]`;

const SEARCH_SAMPLE = `[
  {
    "id": "tt5950044",
    "imdb_id": "tt5950044",
    "type": "movie",
    "name": "Superman",
    "releaseInfo": "2025"
  },
  {
    "id": "tt11192306",
    "imdb_id": "tt11192306",
    "type": "series",
    "name": "Superman & Lois",
    "releaseInfo": "2021-2024"
  }
]`;

const GET_SAMPLE = `{
  "awards": "Won 59 Primetime Emmys. 396 wins & 655 nominations total",
  "cast": [
    "Emilia Clarke",
    "Peter Dinklage",
    "Kit Harington"
  ],
  "country": "United States, United Kingdom",
  "description": "Nine noble families fight for control over the lands of Westeros, while an ancient enemy returns after being dormant for millennia.",
  "director": null,
  "dvdRelease": null,
  "genre": [
    "Action",
    "Adventure",
    "Drama"
  ],
  "imdb_id": "tt0944947",
  "name": "Game of Thrones",
  "released": "2011-04-17T00:00:00.000Z",
  "runtime": "57 min",
  "status": "Ended",
  "tvdb_id": 121361,
  "type": "series",
  "writer": [
    "David Benioff",
    "D.B. Weiss"
  ],
  "year": "2011–2019",
  "moviedb_id": 1399,
  "trailers": [
    {
      "source": "KPLWWIOCOOQ",
      "type": "Trailer"
    },
    {
      "source": "BpJYNVhGf1s",
      "type": "Trailer"
    }
  ],
  "id": "tt0944947",
  "genres": [
    "Action",
    "Adventure",
    "Drama"
  ],
  "releaseInfo": "2011–2019",
  "videos": [
    {
      "name": "Inside Game of Thrones",
      "season": 0,
      "number": 1,
      "firstAired": "2010-12-06T05:00:00.000Z",
      "tvdb_id": 3226241,
      "overview": "A short look into the film-making process for the production Game of Thrones",
      "id": "tt0944947:0:1",
      "released": "2010-12-06T05:00:00.000Z",
      "episode": 1,
      "description": "A short look into the film-making process for the production Game of Thrones"
    },
    {
      "name": "15-Minute Preview",
      "season": 0,
      "number": 2,
      "firstAired": "2011-04-04T05:00:00.000Z",
      "tvdb_id": 4045941,
      "overview": "An approximately 15-minute preview of the first episode of Game of Thrones.",
      "id": "tt0944947:0:2",
      "released": "2011-04-04T05:00:00.000Z",
      "episode": 2,
      "description": "An approximately 15-minute preview of the first episode of Game of Thrones."
    }
  ]
}`;

const API_KEY_SAMPLE = `{
  "api_key": "mh_…",
  "plan": "yearly",
  "created": "2026-06-17T10:00:00.000Z"
}`;

const USAGE_SAMPLE = `{
  "api_key": "mh_…",
  "plan": "yearly",
  "created": "2026-06-17T10:00:00.000Z",
  "quota": 3000,
  "used": 412,
  "today": 17,
  "total": 9021,
  "daily": [
    { "date": "2026-08-23", "count": 0 },
    { "date": "2026-08-24", "count": 3 },
    { "date": "2026-09-21", "count": 17 }
  ]
}`;

const TOC = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'find', label: '/v1/find' },
  { id: 'search', label: '/v1/search' },
  { id: 'get', label: '/v1/get' },
  { id: 'errors', label: 'Errors' },
  { id: 'account', label: 'Account' },
];
const IDS = TOC.map((t) => t.id);

function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: '-35% 0px -55% 0px' },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [ids]);
  return active;
}

function CodeBlock({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={s.codeCard}>
      <div className={s.codeBar}>
        <span>{label}</span>
        <button type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className={s.code}>{code}</pre>
    </div>
  );
}

type Param = { name: string; required: boolean; description: string };
type Field = { name: string; description: ReactNode };

const TRAILERS_FIELD: Field = {
  name: 'trailers',
  description: (
    <>
      YouTube video IDs. Each <code>source</code> is the ID of a YouTube video:{' '}
      <code>https://www.youtube.com/watch?v=&lt;source&gt;</code>
    </>
  ),
};

function FieldsTable({ rows }: { rows: Field[] }) {
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>Field</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>
                <code>{r.name}</code>
              </td>
              <td>{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Params({ rows }: { rows: Param[] }) {
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>Parameter</th>
            <th className={s.narrow}></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>
                <code>{r.name}</code>
              </td>
              <td className={s.narrow}>
                <span className={r.required ? s.req : s.opt}>{r.required ? 'Required' : 'Optional'}</span>
              </td>
              <td>{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type EndpointProps = {
  id: string;
  path: string;
  title: string;
  description: string;
  params: Param[];
  request: string;
  response: string;
  fields?: Field[];
  note?: string;
};

function Endpoint({ id, path, title, description, params, request, response, fields, note }: EndpointProps) {
  return (
    <section id={id} className={s.section}>
      <div className={s.endpoint}>
        <span className={s.method}>GET</span>
        <span className={s.path}>{path}</span>
      </div>
      <h2>{title}</h2>
      <p>{description}</p>
      <span className={s.sub}>Parameters</span>
      <Params rows={params} />
      <span className={s.sub}>Request</span>
      <CodeBlock label="curl" code={request} />
      <span className={s.sub}>Response</span>
      <CodeBlock label="200 OK · application/json" code={response} />
      {note && <p className={s.note}>{note}</p>}
      {fields && (
        <>
          <span className={s.sub}>Fields</span>
          <FieldsTable rows={fields} />
        </>
      )}
    </section>
  );
}

export default function ApiReference() {
  useTitle('API Reference — MetaHub');
  const active = useActiveSection(IDS);

  return (
    <>
      <section className={s.head}>
        <Container>
          <div className={s.kicker}>API Reference</div>
          <h1>MetaHub API reference.</h1>
          <p>Three GET endpoints, JSON in and out. Every request needs an API key from an active MetaHub plan.</p>
          <div className={s.actions}>
            <Button to="/pricing">Get API access</Button>
            <Button to="/docs" variant="ghost">
              Try it in the playground
            </Button>
          </div>
        </Container>
      </section>

      <section className={s.body}>
        <Container>
          <div className={s.layout}>
            <nav className={s.toc} aria-label="On this page">
              {TOC.map((t) => (
                <a key={t.id} href={`#${t.id}`} className={active === t.id ? s.active : ''}>
                  {t.label}
                </a>
              ))}
            </nav>

            <div className={s.content}>
              <section id="authentication" className={s.section}>
                <h2>Authentication</h2>
                <p>
                  The base URL is <code>{BASE_URL}</code>. Pass your API key with every request, either as a query
                  parameter or a header. Your key is on the <Link to="/account">account page</Link>.
                </p>
                <span className={s.sub}>Either of</span>
                <CodeBlock label="api key" code={'?api_key=YOUR_KEY\nX-API-Key: YOUR_KEY'} />
                <p className={s.note}>
                  Requests without a valid key return <code>401</code> with{' '}
                  <code>{'{ "error": "invalid or missing api key" }'}</code>.
                </p>
              </section>

              <Endpoint
                id="find"
                path="/v1/find"
                title="Popular titles"
                description="Returns the most popular movies or series."
                params={[
                  { name: 'type', required: true, description: 'movie or series' },
                  { name: 'limit', required: false, description: 'Number of results (default 10)' },
                ]}
                request={`curl "${BASE_URL}/v1/find?type=movie&limit=1&api_key=YOUR_KEY"`}
                response={FIND_SAMPLE}
                fields={[TRAILERS_FIELD]}
              />

              <Endpoint
                id="search"
                path="/v1/search"
                title="Search"
                description="Searches movies and series by title. Movies are listed first, then series."
                params={[
                  { name: 'q', required: true, description: 'Search query' },
                  { name: 'limit', required: false, description: 'Max results per type (default 10)' },
                ]}
                request={`curl "${BASE_URL}/v1/search?q=superman&limit=1" -H "X-API-Key: YOUR_KEY"`}
                response={SEARCH_SAMPLE}
              />

              <Endpoint
                id="get"
                path="/v1/get"
                title="Title metadata"
                description="Returns full metadata for a single title. Series include a videos array with one entry per episode."
                params={[{ name: 'imdb_id', required: true, description: 'IMDb id, e.g. tt0944947' }]}
                request={`curl "${BASE_URL}/v1/get?imdb_id=tt0944947&api_key=YOUR_KEY"`}
                response={GET_SAMPLE}
                note="videos is truncated to two entries in this example."
                fields={[
                  TRAILERS_FIELD,
                  {
                    name: 'videos',
                    description: (
                      <>
                        Episodes, series only. Each <code>id</code> is <code>imdb_id:season:episode</code>.
                      </>
                    ),
                  },
                ]}
              />

              <section id="errors" className={s.section}>
                <h2>Errors</h2>
                <p>Only a missing or invalid key changes the HTTP status. Other problems return 200 with a JSON body.</p>
                <span className={s.sub}>Responses</span>
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Body</th>
                        <th>When</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <code>401</code>
                        </td>
                        <td>
                          <code>{'{ "error": "invalid or missing api key" }'}</code>
                        </td>
                        <td>Missing or invalid API key</td>
                      </tr>
                      <tr>
                        <td>
                          <code>200</code>
                        </td>
                        <td>
                          <code>{'{ "Error": "Please fill in the required fields and try again." }'}</code>
                        </td>
                        <td>Missing required parameter</td>
                      </tr>
                      <tr>
                        <td>
                          <code>200</code>
                        </td>
                        <td>
                          <code>{'{}'}</code>
                        </td>
                        <td>Unknown type, unknown id, or no data</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="account" className={s.section}>
                <h2>Account</h2>
                <p>
                  These endpoints power the <Link to="/account">dashboard</Link> and use your Stremio session instead of
                  the API key: <code>Authorization: Bearer &lt;stremio authKey&gt;</code>. Both return{' '}
                  <code>404</code> with <code>{'{ "error": "no api key" }'}</code> if the account has no active
                  subscription.
                </p>
                <span className={s.sub}>GET /account/api-key</span>
                <CodeBlock label="200 OK · application/json" code={API_KEY_SAMPLE} />
                <span className={s.sub}>GET /account/usage</span>
                <CodeBlock label="200 OK · application/json" code={USAGE_SAMPLE} />
                <span className={s.sub}>Fields</span>
                <FieldsTable
                  rows={[
                    { name: 'quota', description: 'Monthly request allowance for your plan' },
                    { name: 'used', description: 'Requests this calendar month (UTC)' },
                    { name: 'today', description: 'Requests today (UTC)' },
                    { name: 'total', description: 'All-time requests' },
                    { name: 'daily', description: 'Last 30 days, oldest first' },
                  ]}
                />
              </section>

              <div className={s.cta}>
                <div>
                  <h3>Need a key?</h3>
                  <p>Monthly and Yearly plans include 3,000 requests a month. Lifetime includes 5,000.</p>
                </div>
                <Button to="/pricing">See plans</Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
